/**
 * Copyright (c) 2023 Gitpod GmbH. All rights reserved.
 * Licensed under the GNU Affero General Public License (AGPL).
 * See License.AGPL.txt in the project root for license information.
 */

import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu";
import { cva } from "class-variance-authority";
import { Check, ChevronRight, Search } from "lucide-react";
import * as React from "react";

import type { BaseIconProps } from "@/assets/icons/utils";
import { type PropsWithClassName, cn } from "@/components/podkit/lib/cn";

const DropdownMenu = DropdownMenuPrimitive.Root;

const DropdownMenuTrigger = DropdownMenuPrimitive.Trigger;

const DropdownMenuGroup = DropdownMenuPrimitive.Group;

const DropdownMenuPortal = DropdownMenuPrimitive.Portal;

const DropdownMenuSub = DropdownMenuPrimitive.Sub;

const DropdownMenuRadioGroup = DropdownMenuPrimitive.RadioGroup;

const DropdownMenuSubContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubContent>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubContent> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.SubContent
        ref={ref}
        className={cn(
            "z-50 min-w-[8rem] overflow-hidden rounded-lg border border-border-base bg-surface-popover p-0 py-1 shadow",
            "outline-none focus:outline-none focus-visible:ring-0",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2",
            "data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
            className,
        )}
        {...props}
    />
));
DropdownMenuSubContent.displayName = DropdownMenuPrimitive.SubContent.displayName;

const DropdownMenuContent = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Content> & PropsWithClassName
>(({ className, sideOffset = 4, align = "center", onContextMenu, ...props }, ref) => (
    <DropdownMenuPrimitive.Portal>
        <DropdownMenuPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            align={align}
            className={cn(
                "z-50 w-48 min-w-[8rem] overflow-hidden rounded-md",
                "border border-border-light bg-surface-pure p-1 shadow-sm",
                "w-64 rounded-lg border-border-base bg-surface-popover p-0 shadow first:pt-1 last:pb-1",
                "outline-none focus:outline-none focus-visible:ring-0",
                "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
                className,
            )}
            // Suppress the native context menu on right-click. In modal mode,
            // Radix sets body.style.pointerEvents="none" and relies on its own
            // cleanup to restore it. A native context menu opening on top of
            // the dropdown interferes with that cleanup, leaving the page stuck
            // with pointer-events:none after both menus close.
            onContextMenu={(e) => {
                e.preventDefault();
                onContextMenu?.(e);
            }}
            {...props}
        />
    </DropdownMenuPrimitive.Portal>
));
DropdownMenuContent.displayName = DropdownMenuPrimitive.Content.displayName;

const dropdownMenuItemVariants = cva(
    cn(
        "relative flex cursor-default select-none items-center rounded px-2 py-1.5 text-sm",
        "cursor-pointer hover:bg-surface-hover",
        "text-base focus:bg-surface-hover mx-1 h-8",
        // We explicitly override the default focus from index.css here as we don't want the ring outline; we're using
        // the background color to indicate focus
        "focus:bg-surface-hover focus:text-content-primary focus-visible:ring-0",
        // Virtual highlight for the first item while the search input has focus
        "data-[search-highlighted]:bg-surface-hover",
        "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
    ),
    {
        variants: {
            variant: {
                default: "",
                destructive:
                    "text-content-destructive hover:text-content-destructive focus:text-content-destructive data-[search-highlighted]:text-content-destructive",
            },
        },
        defaultVariants: {
            variant: "default",
        },
    },
);

const DropdownMenuSubTrigger = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.SubTrigger>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.SubTrigger> &
        PropsWithClassName<{
            inset?: boolean;
        }>
>(({ className, inset, children, ...props }, ref) => (
    <DropdownMenuPrimitive.SubTrigger
        ref={ref}
        className={cn(
            dropdownMenuItemVariants({ variant: "default" }),
            "data-[state=open]:bg-surface-hover",
            inset && "pl-8",
            className,
        )}
        {...props}
    >
        {children}
        <ChevronRight className="ml-auto size-4" />
    </DropdownMenuPrimitive.SubTrigger>
));
DropdownMenuSubTrigger.displayName = DropdownMenuPrimitive.SubTrigger.displayName;

const DropdownMenuItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Item>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Item> &
        PropsWithClassName<{
            inset?: boolean;
            variant?: "default" | "destructive";
            LeadingIcon?: React.ComponentType<BaseIconProps>;
        }>
>(({ className, LeadingIcon, children, inset, onClick, variant, asChild, ...props }, ref) => (
    <DropdownMenuPrimitive.Item
        ref={ref}
        className={cn(dropdownMenuItemVariants({ variant }), inset && "pl-8", className)}
        onClick={(e) => {
            if (onClick) {
                e.stopPropagation();
                // Defer the handler so Radix can clear body.style.pointerEvents
                // before the callback runs. Without this, opening a modal (e.g.
                // delete-environment dialog) from a menu item causes the modal
                // and the dropdown to fight over pointer-events:none on <body>,
                // leaving the page unresponsive after both close.
                // See the identical workaround in ContextMenuItem.
                setTimeout(() => onClick(e), 0);
            }
        }}
        asChild={asChild}
        {...props}
    >
        {asChild ? (
            children
        ) : (
            <>
                {LeadingIcon ? (
                    <span className="mr-2">
                        <LeadingIcon size="sm" />
                    </span>
                ) : null}
                {children}
            </>
        )}
    </DropdownMenuPrimitive.Item>
));
DropdownMenuItem.displayName = DropdownMenuPrimitive.Item.displayName;

const DropdownMenuCheckboxItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.CheckboxItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.CheckboxItem> & PropsWithClassName
>(({ className, children, checked, ...props }, ref) => (
    <DropdownMenuPrimitive.CheckboxItem
        ref={ref}
        className={cn(
            "relative flex cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none transition-colors",
            "focus:bg-surface-tertiary focus:text-content-primary focus-visible:ring-0",
            "hover:cursor-pointer",
            "data-[disabled]:pointer-events-none data-[disabled]:opacity-50",
            className,
        )}
        checked={checked}
        {...props}
    >
        <span className={cn("absolute left-2 flex h-3.5 w-3.5 items-center justify-center")}>
            <DropdownMenuPrimitive.ItemIndicator>
                <Check className="size-4" />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.CheckboxItem>
));
DropdownMenuCheckboxItem.displayName = DropdownMenuPrimitive.CheckboxItem.displayName;

const DropdownMenuRadioItem = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.RadioItem>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.RadioItem> & PropsWithClassName
>(({ className, children, ...props }, ref) => (
    <DropdownMenuPrimitive.RadioItem
        ref={ref}
        className={cn(dropdownMenuItemVariants({ variant: "default" }), "pl-8", className)}
        {...props}
    >
        <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
            <DropdownMenuPrimitive.ItemIndicator>
                <Check className="size-4" />
            </DropdownMenuPrimitive.ItemIndicator>
        </span>
        {children}
    </DropdownMenuPrimitive.RadioItem>
));
DropdownMenuRadioItem.displayName = DropdownMenuPrimitive.RadioItem.displayName;

const DropdownMenuLabel = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Label>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Label> &
        PropsWithClassName<{
            inset?: boolean;
        }>
>(({ className, inset, ...props }, ref) => (
    <DropdownMenuPrimitive.Label
        ref={ref}
        className={cn("mx-1 px-2 py-1.5 text-sm font-medium text-content-muted", inset && "pl-8", className)}
        {...props}
    />
));
DropdownMenuLabel.displayName = DropdownMenuPrimitive.Label.displayName;

const DropdownMenuSeparator = React.forwardRef<
    React.ElementRef<typeof DropdownMenuPrimitive.Separator>,
    React.ComponentPropsWithoutRef<typeof DropdownMenuPrimitive.Separator> & PropsWithClassName
>(({ className, ...props }, ref) => (
    <DropdownMenuPrimitive.Separator
        ref={ref}
        className={cn("my-1 h-px bg-content-tertiary/20", className)}
        {...props}
    />
));
DropdownMenuSeparator.displayName = DropdownMenuPrimitive.Separator.displayName;

const DropdownMenuShortcut = ({ className, ...props }: React.HTMLAttributes<HTMLSpanElement> & PropsWithClassName) => {
    return <span className={cn("ml-auto text-xs tracking-widest opacity-60", className)} {...props} />;
};
DropdownMenuShortcut.displayName = "DropdownMenuShortcut";

// ---------------------------------------------------------------------------
// Searchable sub-content primitives
// ---------------------------------------------------------------------------

/**
 * Collects all `[role="menuitem"]` elements inside the closest menu root.
 */
function getMenuItems(el: HTMLElement | null): HTMLElement[] {
    if (!el) return [];
    const root = el.closest('[role="menu"]');
    if (!root) return [];
    return Array.from(root.querySelectorAll<HTMLElement>('[role="menuitem"]'));
}

type DropdownMenuSubContentSearchProps = PropsWithClassName<{
    /** Controlled search value */
    value: string;
    /** Called when the search value changes */
    onValueChange: (value: string) => void;
    /** Placeholder text shown when the input is empty */
    placeholder?: string;
}>;

/**
 * A search input pinned to the top of a `DropdownMenuSubContent`.
 *
 * Focus model:
 * - ArrowDown moves focus to the first menu item; Radix handles
 *   subsequent arrow navigation and `data-highlighted`.
 * - ArrowUp from the first item returns focus to the input.
 * - Typing a character while a menu item is focused redirects focus
 *   back to the input and appends the character to the search value.
 * - Enter from the input selects the first visible item.
 * - Escape is not intercepted (Radix closes the sub-menu).
 */
const DropdownMenuSubContentSearch = React.forwardRef<HTMLInputElement, DropdownMenuSubContentSearchProps>(
    ({ className, value, onValueChange, placeholder = "Search…" }, forwardedRef) => {
        const inputRef = React.useRef<HTMLInputElement>(null);
        React.useImperativeHandle(forwardedRef, () => inputRef.current!);

        // Clear the search value when the sub-menu closes (component unmounts)
        const onValueChangeRef = React.useRef(onValueChange);
        onValueChangeRef.current = onValueChange;
        React.useEffect(() => {
            return () => onValueChangeRef.current("");
        }, []);

        // Manage a virtual highlight on the first menu item while the input is focused.
        // Radix only sets data-highlighted when an item has real DOM focus, but we want
        // a visual indicator of what Enter will select while the user is typing.
        React.useEffect(() => {
            const input = inputRef.current;
            if (!input) return;
            const menuRoot = input.closest('[role="menu"]');
            if (!menuRoot) return;

            const ATTR = "data-search-highlighted";

            const clearHighlight = () => {
                for (const el of menuRoot.querySelectorAll(`[${ATTR}]`)) {
                    el.removeAttribute(ATTR);
                }
            };

            const syncHighlight = () => {
                clearHighlight();
                if (document.activeElement === input) {
                    const first = menuRoot.querySelector<HTMLElement>('[role="menuitem"]');
                    first?.setAttribute(ATTR, "");
                }
            };

            const handleFocus = () => syncHighlight();
            const handleBlur = () => clearHighlight();

            input.addEventListener("focus", handleFocus);
            input.addEventListener("blur", handleBlur);

            // Re-sync when items are added/removed (handles async/remote data)
            const observer = new MutationObserver(syncHighlight);
            observer.observe(menuRoot, { childList: true, subtree: true });

            // Initial sync (input may already be focused)
            syncHighlight();

            return () => {
                input.removeEventListener("focus", handleFocus);
                input.removeEventListener("blur", handleBlur);
                observer.disconnect();
                clearHighlight();
            };
        }, [inputRef]);

        // Listen on the menu root for keydown events that bubble up from focused menu items.
        // This handles: arrow-up-at-top → refocus input, character keys → redirect to input.
        React.useEffect(() => {
            const input = inputRef.current;
            if (!input) return;
            const menuRoot = input.closest('[role="menu"]');
            if (!menuRoot) return;

            const handleMenuKeyDown = (evt: Event) => {
                const e = evt as KeyboardEvent;
                const target = e.target as HTMLElement;

                // Only intercept events from menu items, not from the input itself
                if (!target || target === input || target.getAttribute("role") !== "menuitem") return;

                const items = getMenuItems(input);
                const isFirstItem = items.length > 0 && target === items[0];

                if (e.key === "ArrowUp" && isFirstItem) {
                    // Move focus back to the search input from the first item
                    e.preventDefault();
                    e.stopPropagation();
                    input.focus();
                    return;
                }

                const isModifier = e.ctrlKey || e.altKey || e.metaKey;
                if (!isModifier && e.key.length === 1) {
                    // Redirect character input back to the search field
                    e.preventDefault();
                    e.stopPropagation();
                    onValueChange(value + e.key);
                    input.focus();
                    return;
                }

                if (e.key === "Backspace") {
                    e.preventDefault();
                    e.stopPropagation();
                    onValueChange(value.slice(0, -1));
                    input.focus();
                    return;
                }
            };

            // Use capture phase to intercept before Radix's typeahead handler
            menuRoot.addEventListener("keydown", handleMenuKeyDown, true);
            return () => menuRoot.removeEventListener("keydown", handleMenuKeyDown, true);
        }, [inputRef, onValueChange, value]);

        const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
            const items = getMenuItems(inputRef.current);

            switch (e.key) {
                case "ArrowDown": {
                    e.preventDefault();
                    e.stopPropagation();
                    // The first item is already visually highlighted via data-search-highlighted,
                    // so ArrowDown moves to the second item (or the first if only one exists).
                    (items[1] ?? items[0])?.focus();
                    break;
                }
                case "Enter": {
                    e.preventDefault();
                    e.stopPropagation();
                    // Select the first visible item
                    items[0]?.click();
                    break;
                }
                case "ArrowLeft": {
                    // When the input is empty, let the event propagate so Radix closes the sub-menu
                    if (value === "") break;
                    e.stopPropagation();
                    break;
                }
                default:
                    // Stop propagation to prevent Radix typeahead from consuming characters
                    e.stopPropagation();
                    break;
            }
        };

        return (
            <div className={cn("mb-1 flex items-center gap-2 border-b border-border-base px-3 py-2", className)}>
                <Search size={14} className="flex-none text-content-secondary" />
                <input
                    ref={inputRef}
                    type="text"
                    placeholder={placeholder}
                    value={value}
                    onChange={(e) => onValueChange(e.target.value)}
                    className="w-full bg-transparent text-sm text-content-primary placeholder:text-content-secondary focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    data-tracking-id="dropdown-sub-content-search"
                />
            </div>
        );
    },
);
DropdownMenuSubContentSearch.displayName = "DropdownMenuSubContentSearch";

/**
 * Empty state shown when a searchable sub-menu has no matching items.
 * Renders "No results" by default; pass children to override.
 */
const DropdownMenuSubContentEmpty: React.FC<PropsWithClassName<{ children?: React.ReactNode }>> = ({
    className,
    children,
}) => <div className={cn("px-3 py-2 text-sm text-content-secondary", className)}>{children ?? "No results"}</div>;
DropdownMenuSubContentEmpty.displayName = "DropdownMenuSubContentEmpty";

export {
    DropdownMenu,
    DropdownMenuCheckboxItem,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
    DropdownMenuSeparator,
    DropdownMenuShortcut,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubContentEmpty,
    DropdownMenuSubContentSearch,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger,
};
